import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';

// Type definitions based on API specification
export interface LtecBatch {
    batch_id: string;
    tff_date: string | null;
    total_yield_g: number | null;
    aliquot_yield_g: number | null;
    aliquot_volume_ml: number | null;
    num_aliquots_produced: number | null;
    number_aliquots_required_per_administration: number | null;
    number_reference_aliquots: number | null;
    unknown_aliquot_offset: number | null;
    approved_for_usage: boolean | null;
    passed_qc: boolean | null;
}

export interface LtecBatchCreate {
    batch_id: string;
    tff_date: string | null;
    total_yield_g: number | null;
    aliquot_yield_g: number | null;
    aliquot_volume_ml: number | null;
    num_aliquots_produced: number | null;
    number_aliquots_required_per_administration: number | null;
    number_reference_aliquots: number | null;
    unknown_aliquot_offset: number | null;
    approved_for_usage: boolean | null;
    passed_qc: boolean | null;
}

export interface LtecBatchUpdate {
    tff_date?: string | null;
    total_yield_g?: number | null;
    aliquot_yield_g?: number | null;
    aliquot_volume_ml?: number | null;
    num_aliquots_produced?: number | null;
    number_aliquots_required_per_administration?: number | null;
    number_reference_aliquots?: number | null;
    unknown_aliquot_offset?: number | null;
    approved_for_usage?: boolean | null;
    passed_qc?: boolean | null;
}

interface LtecBatchState {
    batches: { [key: string]: LtecBatch };
    isLoading: boolean;
    error: string | null;
    selectedBatchId: string | null;
}

const initialState: LtecBatchState = {
    batches: {},
    isLoading: false,
    error: null,
    selectedBatchId: null,
};

// Helper function to calculate remaining aliquots
const calculateRemainingAliquots = (batch: LtecBatch): number => {
    // This is a placeholder calculation - adjust based on business logic
    // For now, assuming remaining = produced - reference aliquots
    const numProduced = batch.num_aliquots_produced || 0;
    const numReference = batch.number_reference_aliquots || 0;
    return Math.max(0, numProduced - numReference);
};

const ltecBatchSlice = createSlice({
    name: 'ltecBatch',
    initialState,
    reducers: {
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        setBatches: (state, action: PayloadAction<LtecBatch[]>) => {
            state.batches = {};
            action.payload.forEach(batch => {
                state.batches[batch.batch_id] = batch;
            });
            state.error = null;
        },
        addBatch: (state, action: PayloadAction<LtecBatch>) => {
            const batch = action.payload;
            state.batches[batch.batch_id] = batch;
        },
        updateBatch: (state, action: PayloadAction<LtecBatch>) => {
            const batch = action.payload;
            if (state.batches[batch.batch_id]) {
                state.batches[batch.batch_id] = batch;
            }
        },
        removeBatch: (state, action: PayloadAction<string>) => {
            const batchId = action.payload;
            if (state.batches[batchId]) {
                delete state.batches[batchId];
            }
            // Clear selection if deleted batch was selected
            if (state.selectedBatchId === batchId) {
                state.selectedBatchId = null;
            }
        },
        setSelectedBatch: (state, action: PayloadAction<string | null>) => {
            state.selectedBatchId = action.payload;
        },
    },
});

export const {
    setLoading,
    setError,
    setBatches,
    addBatch,
    updateBatch,
    removeBatch,
    setSelectedBatch,
} = ltecBatchSlice.actions;

// Selectors
export const selectBatchesMap = (state: { ltecBatch: LtecBatchState }) => state.ltecBatch.batches;
export const selectBatchesLoading = (state: { ltecBatch: LtecBatchState }) => state.ltecBatch.isLoading;
export const selectBatchesError = (state: { ltecBatch: LtecBatchState }) => state.ltecBatch.error;
export const selectSelectedBatchId = (state: { ltecBatch: LtecBatchState }) => state.ltecBatch.selectedBatchId;

export const selectAllBatches = createSelector(
    (state: { ltecBatch: LtecBatchState }) => state.ltecBatch.batches,
    (batchesMap) => Object.values(batchesMap)
);

export const selectSelectedBatch = (state: { ltecBatch: LtecBatchState }) => {
    const selectedId = state.ltecBatch.selectedBatchId;
    return selectedId ? state.ltecBatch.batches[selectedId] : null;
};

const selectBatchById = (state: { ltecBatch: LtecBatchState }, batchId: string) =>
    state.ltecBatch.batches[batchId];

export const selectBatchesCount = (state: { ltecBatch: LtecBatchState }) =>
    Object.keys(state.ltecBatch.batches).length;

// Calculated selectors
export const selectApprovedBatches = createSelector(
    selectAllBatches,
    (allBatches) => allBatches.filter(batch => batch.approved_for_usage)
);

export default ltecBatchSlice.reducer; 